import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import {
  Smile,
  Reply,
  Bookmark,
  Download,
  Star,
  Copy,
  Check,
  Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { downloadMedia } from "@/lib/mediaDownload";
import {
  getQuickReactions,
  registerReactionUse,
} from "@/pages/internal/inbox/utils/quickReactions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Lazy load Picker to optimize bundle size
const Picker = lazy(() => import("@emoji-mart/react"));
import data from "@emoji-mart/data";

const MEDIA_TYPES = ["image", "video", "audio", "document", "sticker"];

export function MessageActions({
  className,
  onReact,
  onReply,
  onSave,
  onAddStickerFavorite,
  onPickerOpenChange,
  showReact = true,
  /**
   * Nem todo lugar que usa este componente tem "Salvar".
   *
   * O canal do Lucas, por exemplo, não tem mensagens salvas — e um botão que
   * não faz nada é pior que botão nenhum. O padrão continua `true` para o Inbox
   * seguir exatamente como estava.
   */
  showSave = true,
  messageType,
  mediaUrl,
  copyText,
  mediaFilename,
  /**
   * Link direto para esta mensagem (`?ticket=…&message=…`). Só existe onde há
   * ticket — o chat do Lucas e os grupos internos passam sem ela.
   */
  linkUrl,
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Lido uma vez por montagem: reordenar a barra embaixo do ponteiro, logo após
  // o clique, moveria os botões vizinhos de lugar no meio da interação.
  const [quickReactions] = useState(getQuickReactions);

  const handleQuickReact = useCallback(
    (emoji) => {
      registerReactionUse(emoji);
      onReact?.(emoji);
    },
    [onReact],
  );

  // Reset copied state after 1.5s
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (!linkCopied) return;
    const t = setTimeout(() => setLinkCopied(false), 1500);
    return () => clearTimeout(t);
  }, [linkCopied]);

  const handleCopy = useCallback(() => {
    if (!copyText) return;
    navigator.clipboard
      .writeText(copyText)
      .then(() => setCopied(true))
      .catch(() => {});
  }, [copyText]);

  const handleCopyLink = useCallback(() => {
    if (!linkUrl) return;
    navigator.clipboard
      .writeText(linkUrl)
      .then(() => setLinkCopied(true))
      .catch(() => {});
  }, [linkUrl]);

  const handlePickerChange = (open) => {
    setShowPicker(open);
    onPickerOpenChange?.(open);
  };

  const handleEmojiSelect = (emoji) => {
    // Contabiliza também o que veio do picker: é assim que um emoji novo sobe
    // para os atalhos.
    registerReactionUse(emoji.native);
    onReact?.(emoji.native);
    handlePickerChange(false);
  };

  const handleDownload = () => downloadMedia(mediaUrl, mediaFilename);

  const isMedia = MEDIA_TYPES.includes(messageType);

  return (
    <div
      className={cn(
        "relative flex items-center gap-0.5 bg-surface border border-border rounded-lg p-1 shadow-lg",
        // `opacity-0` esconde mas NÃO desliga o ponteiro: a barra invisível
        // continuava capturando clique e seleção de texto da mensagem que ela
        // sobrepõe. Como ela fica por cima da mensagem de cima, era um trecho
        // morto de ~36px em cada linha da conversa.
        "opacity-0 pointer-events-none transition-opacity duration-200",
        "group-hover:opacity-100 group-hover:pointer-events-auto",
        className,
        // Com o picker aberto o ponteiro sai da linha da mensagem; sem forçar
        // aqui, a barra morreria no meio da escolha do emoji.
        showPicker && "opacity-100 pointer-events-auto",
      )}
    >
      <TooltipProvider delayDuration={300}>
        {/* Reações rápidas — os mais usados, a um clique */}
        {showReact &&
          quickReactions.map((emoji) => (
            <Tooltip key={emoji}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Reagir com ${emoji}`}
                  onClick={() => handleQuickReact(emoji)}
                  className="h-7 w-7 text-base leading-none hover:bg-surface-hover hover:scale-110 transition-transform"
                >
                  <span aria-hidden="true">{emoji}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-surface border-border text-xs">
                Reagir com {emoji}
              </TooltipContent>
            </Tooltip>
          ))}

        {showReact && <span className="w-px h-4 bg-border mx-0.5" />}

        {/* Reagir */}
        {showReact && (
          <Popover open={showPicker} onOpenChange={handlePickerChange}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <div>
                    <ActionButton icon={Smile} label="Reagir" active={showPicker} />
                  </div>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent className="bg-surface border-border text-xs">
                Reagir
              </TooltipContent>
            </Tooltip>

            <PopoverContent
              side="top"
              align="end"
              sideOffset={10}
              className="w-auto p-0 border-none bg-transparent shadow-none data-[side=top]:mb-2 z-[9999]"
            >
              <Suspense
                fallback={
                  <div className="w-[280px] h-[280px] bg-surface rounded-lg border border-border flex items-center justify-center">
                    <span className="text-foreground-muted text-sm">
                      Carregando...
                    </span>
                  </div>
                }
              >
                <div className="shadow-2xl rounded-xl overflow-hidden border border-border bg-surface">
                  <Picker
                    data={data}
                    onEmojiSelect={handleEmojiSelect}
                    theme="dark"
                    locale="pt"
                    perLine={7}
                    maxHeight={180}
                    emojiSize={18}
                    navPosition="top"
                    searchPosition="sticky"
                    previewPosition="none"
                  />
                </div>
              </Suspense>
            </PopoverContent>
          </Popover>
        )}

        {/* Responder */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ActionButton icon={Reply} label="Responder" onClick={onReply} />
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-surface border-border text-xs">
            Responder
          </TooltipContent>
        </Tooltip>

        {/* Copiar */}
        {copyText && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ActionButton
                  icon={copied ? Check : Copy}
                  label={copied ? "Copiado" : "Copiar"}
                  onClick={handleCopy}
                  active={copied}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-surface border-border text-xs">
              {copied ? "Copiado!" : "Copiar"}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Copiar link desta mensagem */}
        {linkUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ActionButton
                  icon={linkCopied ? Check : Link2}
                  label={linkCopied ? "Link copiado" : "Copiar link"}
                  onClick={handleCopyLink}
                  active={linkCopied}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-surface border-border text-xs">
              {linkCopied ? "Link copiado!" : "Copiar link da mensagem"}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Salvar */}
        {showSave && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ActionButton icon={Bookmark} label="Salvar" onClick={onSave} />
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-surface border-border text-xs">
              Salvar
            </TooltipContent>
          </Tooltip>
        )}

        {/* Download — apenas para mídia */}
        {isMedia && mediaUrl && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ActionButton icon={Download} label="Baixar" onClick={handleDownload} />
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-surface border-border text-xs">
              Baixar
            </TooltipContent>
          </Tooltip>
        )}

        {/* Adicionar sticker aos favoritos */}
        {messageType === "sticker" && mediaUrl && onAddStickerFavorite && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ActionButton
                  icon={Star}
                  label="Adicionar aos favoritos"
                  onClick={() => onAddStickerFavorite(mediaUrl)}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-surface border-border text-xs">
              Adicionar aos favoritos
            </TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>
    </div>
  );
}

/**
 * `label` vira o nome acessível do botão.
 *
 * O rótulo só existia no tooltip, que é conteúdo visual: para um leitor de tela
 * — e para um teste — estes botões não tinham nome nenhum.
 */
function ActionButton({ icon: Icon, active, onClick, label, className: cls }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "h-7 w-7 text-foreground-muted hover:text-foreground-strong hover:bg-surface-hover transition-colors",
        active && "text-yellow-400 bg-surface-active",
        cls,
      )}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}
